/**
 * Error handling utility for extracting meaningful error messages
 * from various error sources (axios, API responses, etc.)
 */

interface ErrorResponse {
  message?: string;
  error?: string;
  details?: string;
  stack?: string;
}

/**
 * Extracts a user-friendly error message from an error object
 * @param error - The error object from a catch block
 * @param fallbackMessage - Default message if no specific error is found
 * @returns A user-friendly error message with context
 */
export function getErrorMessage(error: any, fallbackMessage: string = 'An unexpected error occurred'): string {
  // Check if it's an axios error with response
  if (error?.response) {
    const status = error.response.status;
    const data: ErrorResponse = error.response.data;

    // Extract message from response data
    const serverMessage = data?.message || data?.error || data?.details;
    
    if (serverMessage) {
      // Include status code for better debugging
      return `${serverMessage} (Error ${status})`;
    }

    // Provide status-specific messages if no server message
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please log in and try again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'A conflict occurred. The resource may already exist.';
      case 422:
        return 'Invalid data provided. Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Server error (${status}). Please try again later.`;
    }
  }

  // Check if it's a network error
  if (error?.request && !error?.response) {
    return 'Network error. Please check your connection and try again.';
  }

  // Check if error has a message property
  if (error?.message) {
    // Filter out technical error messages that aren't helpful to users
    const technicalErrors = ['Network Error', 'timeout', 'ECONNREFUSED', 'ENOTFOUND'];
    const isTechnical = technicalErrors.some(term => error.message.includes(term));
    
    if (isTechnical) {
      return 'Connection error. Please check your network and try again.';
    }
    
    return error.message;
  }

  // If error is a string
  if (typeof error === 'string') {
    return error;
  }

  // Fallback to the provided message
  return fallbackMessage;
}

/**
 * Gets a detailed error message for bug reporting
 * @param error - The error object
 * @param context - Additional context about where the error occurred
 * @returns Detailed error information for debugging
 */
export function getDetailedErrorMessage(error: any, context: string): string {
  const baseMessage = getErrorMessage(error);
  const timestamp = new Date().toISOString();
  
  let details = `[${timestamp}] Error in ${context}\n`;
  details += `Message: ${baseMessage}\n`;
  
  if (error?.response) {
    details += `Status: ${error.response.status}\n`;
    details += `URL: ${error.config?.url || 'unknown'}\n`;
    details += `Method: ${error.config?.method?.toUpperCase() || 'unknown'}\n`;
    
    if (error.response.data) {
      details += `Server Response: ${JSON.stringify(error.response.data, null, 2)}\n`;
    }
  }
  
  if (error?.stack) {
    details += `\nStack: ${error.stack}`;
  }
  
  return details;
}

/**
 * Logs an error to the console with context
 * @param error - The error object
 * @param context - Where the error occurred
 */
export function logError(error: any, context: string): void {
  const detailedMessage = getDetailedErrorMessage(error, context);
  console.error(detailedMessage);
}

/**
 * Creates a user-friendly error message with a suggestion to report bugs
 * @param error - The error object
 * @param actionContext - What the user was trying to do
 * @returns Message to display to the user
 */
export function createUserErrorMessage(error: any, actionContext: string): string {
  const errorMessage = getErrorMessage(error);
  return `${errorMessage}\n\nIf this issue persists, please report it with the following context: "${actionContext}"`;
}
